import { Component, type ReactNode } from 'react';

/** Lightweight error fallback - inline styles to ensure it works even if CSS fails */
const ErrorFallback = () => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#fef2f2',
      color: '#991b1b',
      padding: '24px',
      textAlign: 'center',
    }}
  >
    <svg
      width='48'
      height='48'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='12' cy='12' r='10' />
      <line x1='12' y1='8' x2='12' y2='12' />
      <line x1='12' y1='16' x2='12.01' y2='16' />
    </svg>
    <h1 style={{ margin: '16px 0 8px', fontSize: '20px', fontWeight: 600 }}>Something went wrong</h1>
    <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>Please refresh the page or try again later.</p>
    <button
      onClick={() => window.location.reload()}
      style={{
        marginTop: '20px',
        padding: '8px 16px',
        fontSize: '14px',
        fontWeight: 500,
        color: '#fff',
        backgroundColor: '#dc2626',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
      }}
    >
      Refresh Page
    </button>
  </div>
);

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/** Lightweight React Error Boundary - no external dependencies */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[AIR Trainer] Uncaught error:', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? <ErrorFallback />;
    }
    return this.props.children;
  }
}
